import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Loader2, ThumbsUp, ThumbsDown, Send, Eye, Trash2, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";

export default function AITest() {
  return (
    <div>
      <PageTitle title="AI Conversations" subtitle="Review Nexus chats, test routing, and manage stored conversations" />
      <Tabs defaultValue="conversations">
        <TabsList className="mb-6">
          <TabsTrigger value="conversations" data-testid="tab-conversations"><MessageSquare className="h-4 w-4 mr-1.5" /> Conversations</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test"><Bot className="h-4 w-4 mr-1.5" /> Test Nexus</TabsTrigger>
        </TabsList>
        <TabsContent value="conversations"><Conversations /></TabsContent>
        <TabsContent value="test"><TestPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function Conversations() {
  const qc = useQueryClient();
  const [view, setView] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { data: conversations, isLoading } = useQuery({
    queryKey: ["ai-conversations"],
    queryFn: () => api.get("/nexus/conversations").then(r => r.data.conversations),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["ai-conversations"] });

  const removeOne = async (sid) => {
    await api.delete(`/nexus/conversations/${sid}`); refresh(); toast.success("Conversation deleted");
  };
  const clearAll = async () => {
    setClearing(true);
    try {
      const res = await api.delete("/nexus/conversations");
      refresh(); setClearOpen(false);
      toast.success(`Cleared ${res.data.deleted || 0} conversation(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500" data-testid="conversation-count">{(conversations || []).length} stored conversation(s)</p>
        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5" disabled={!(conversations || []).length}
          onClick={() => setClearOpen(true)} data-testid="clear-all-conversations-btn">
          <Trash2 className="h-4 w-4" /> Clear all
        </Button>
      </div>
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Preview</th><th className="text-left p-3">Source</th><th className="text-left p-3">Messages</th><th className="text-left p-3">Lead</th><th className="text-left p-3">Last activity</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={6} className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></td></tr> :
              (conversations || []).length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No conversations yet.</td></tr> :
              (conversations || []).map((c) => (
                <tr key={c.sessionId} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`conversation-row-${c.sessionId}`}>
                  <td className="p-3 text-navy max-w-xs truncate">{c.preview || <span className="text-slate-400 italic">No user messages</span>}</td>
                  <td className="p-3 text-slate-500 capitalize">{c.source || "—"}</td>
                  <td className="p-3 text-slate-500">{c.messageCount}</td>
                  <td className="p-3">{c.leadCaptured ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Lead</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="p-3 text-xs text-slate-400 font-mono">{c.lastActivity ? new Date(c.lastActivity).toLocaleString() : "—"}</td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    <button title="View" onClick={() => setView(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`view-conversation-${c.sessionId}`}><Eye className="h-4 w-4" /></button>
                    <ConfirmDelete onConfirm={() => removeOne(c.sessionId)} title="Delete this conversation?" description="This permanently removes the chat log, messages and routing data. This cannot be undone." testid={`delete-conversation-${c.sessionId}`} />
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {view && <ConversationDialog conv={view} onClose={() => setView(null)} />}

      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all AI conversations?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes every stored Nexus conversation, message and routing record for your workspace. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); clearAll(); }} disabled={clearing} className="bg-red-600 hover:bg-red-700" data-testid="clear-all-confirm">
              {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConversationDialog({ conv, onClose }) {
  const { data: messages, isLoading } = useQuery({
    queryKey: ["ai-conversation", conv.sessionId],
    queryFn: () => api.get(`/nexus/conversations/${conv.sessionId}/messages`).then(r => r.data.messages),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="conversation-dialog">
        <DialogHeader><DialogTitle>Conversation transcript</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isLoading ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> :
            (messages || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No messages.</p> :
            (messages || []).map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-6 w-6 rounded-full grid place-items-center shrink-0 ${m.role === "user" ? "bg-navy" : "bg-electric"}`}>
                  <Bot className="h-3 w-3 text-white" />
                </div>
                <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-navy text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TestPanel() {
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setLoading(true); setResult(null);
    try { const res = await api.post("/ai/test", { message: msg }); setResult(res.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const feedback = async (rating) => {
    try { await api.post("/ai/feedback", null, { params: { rating } }); toast.success("Feedback recorded"); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl bg-white border border-slate-200 p-5">
        <form onSubmit={run} className="space-y-3" data-testid="ai-test-form">
          <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={4} placeholder="e.g. My laptop won't turn on and I need it fixed fast" className="w-full rounded-lg border border-input p-3 text-sm focus:outline-none focus:ring-2 focus:ring-electric" data-testid="ai-test-input" />
          <Button type="submit" disabled={loading} className="bg-electric hover:bg-tech gap-2" data-testid="ai-test-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test Nexus</Button>
        </form>
        <div className="mt-4 flex flex-wrap gap-2">
          {["PC won't boot", "How much for a website?", "Automate my invoices", "Do you serve Salem?"].map((q) => (
            <button key={q} onClick={() => setMsg(q)} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 hover:border-electric text-slate-600">{q}</button>
          ))}
        </div>
      </div>
      <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="ai-test-result">
        {!result ? <div className="text-center py-12 text-slate-400"><Bot className="h-10 w-10 mx-auto mb-2" />Run a test to see routing details.</div> : (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4"><p className="text-sm text-slate-700 whitespace-pre-wrap">{result.reply}</p></div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Meta label="Selected Agent" value={result.agent} />
              <Meta label="Confidence" value={`${Math.round((result.confidence || 0) * 100)}%`} />
              <Meta label="Intent" value={result.intent} />
              <Meta label="Escalate" value={result.escalate ? "Yes" : "No"} />
            </div>
            <div><p className="text-xs font-mono uppercase tracking-wider text-metallic mb-1">Routing reason</p><p className="text-sm text-slate-600">{result.routingReason}</p></div>
            <div><p className="text-xs font-mono uppercase tracking-wider text-metallic mb-1">Knowledge available ({result.knowledgeUsed?.length || 0})</p><div className="flex flex-wrap gap-1">{(result.knowledgeUsed || []).map((k, i) => <span key={i} className="text-xs px-2 py-0.5 rounded bg-electric/10 text-electric">{k}</span>)}</div></div>
            <div className="flex gap-2 pt-2 border-t"><Button variant="outline" size="sm" onClick={() => feedback("up")} data-testid="ai-thumbs-up"><ThumbsUp className="h-4 w-4" /></Button><Button variant="outline" size="sm" onClick={() => feedback("down")} data-testid="ai-thumbs-down"><ThumbsDown className="h-4 w-4" /></Button></div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return <div className="rounded-lg border border-slate-200 p-3"><p className="text-[10px] font-mono uppercase tracking-wider text-metallic">{label}</p><p className="font-medium text-navy capitalize">{value || "—"}</p></div>;
}
